import { useRef } from 'react'
import { useOutsideClick } from '@sequencehq/utils'
import { useAtomValue, useSetAtom } from 'jotai'
import { GreyGrey30, Box } from '@sequencehq/core-components'
import {
  GoCardlessInspectorItem,
  GoCardlessPaymentWidget
} from 'Integrations/index'
import {
  clientInvoiceAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { triggeredInspectorItemIdAtom } from '..'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'
import { usePermissions } from 'lib/rbac'
import { useGoCardlessAvailability } from 'Invoices/hooks/useGoCardlessAvailability'

export const InvoiceGoCardlessInspectorItem = () => {
  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const ref = useRef<HTMLDivElement>(null)
  const setTriggeredInspectorItemId = useSetAtom(triggeredInspectorItemIdAtom)
  const { displayNotification } = useNotifications()
  const isLineItemSavePending = useAtomValue(isSavePendingAtom)
  const { can } = usePermissions()
  const canManageInvoices = can('org.invoice:update')
  const { isEnabled, isAvailable, disabledReason } = useGoCardlessAvailability()

  const { mutateAsync: createPaymentSettings, isPending: createIsPending } =
    useMutation(dashboard20240730Client.postInvoiceSettings)

  useOutsideClick({
    ref,
    handler: () => {
      setTriggeredInspectorItemId(null)
    }
  })

  if (!clientInvoice) return null

  const isDisabled =
    isLineItemSavePending ||
    createIsPending ||
    isEnabled ||
    !isAvailable ||
    clientInvoice.status === 'VOIDED'

  const goCardlessControlsDisabled = !canManageInvoices || isDisabled

  const updateInvoiceSettings = async (
    isGoCardlessPaymentSelected: boolean
  ) => {
    try {
      await createPaymentSettings({
        invoiceId: clientInvoice.id,
        customerId: clientInvoice.customerId,
        paymentProvider: isGoCardlessPaymentSelected ? 'GOCARDLESS' : 'NONE'
      })
      displayNotification('Invoice GoCardless settings updated', {
        type: 'success'
      })
    } catch (error) {
      displayNotification(
        getApiErrorMessage(
          error,
          'Could not update invoice GoCardless settings'
        ),
        { type: 'error' }
      )
    }
  }

  return (
    <GoCardlessInspectorItem enabled={isEnabled}>
      <Box ref={ref}>
        <Box borderTop={`1px solid ${GreyGrey30}`} paddingTop="16px">
          <GoCardlessPaymentWidget
            fieldData={{
              acceptPaymentsViaGoCardless: {
                disabled: isDisabled,
                value: isEnabled
              }
            }}
            onChange={newData => {
              void updateInvoiceSettings(
                newData.acceptPaymentsViaGoCardless.value
              )
            }}
            disabled={goCardlessControlsDisabled}
            disabledReason={disabledReason}
          />
        </Box>
      </Box>
    </GoCardlessInspectorItem>
  )
}
